// @ts-check
/**
 * Shipment management for a single order, rendered inside OrderDetailModal.
 *
 * Saving a shipment moves the order to Shipped and emails the customer their tracking
 * link. Everything after that comes from the carrier, so there is one email per shipment
 * plus an explicit Resend for bounces and corrections.
 */

import { Ban, ExternalLink, Mail, Plus, RefreshCw, Trash2, Truck } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useToast } from '../context/ToastContext';
import ShipmentCharges from './ShipmentCharges';
import adminShippingService from '../services/adminShippingService';

const SHIPMENT_STATUSES = [
  { value: 'created', label: 'Created' },
  { value: 'pickup_scheduled', label: 'Pickup Scheduled' },
  { value: 'in_transit', label: 'In Transit' },
  { value: 'out_for_delivery', label: 'Out for Delivery' },
  { value: 'delivered', label: 'Delivered' },
  { value: 'rto', label: 'Returned (RTO)' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'exception', label: 'Exception' },
];

const statusColor = (status) =>
  ({
    created: 'bg-muted text-foreground',
    pickup_scheduled: 'bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-300',
    in_transit: 'bg-indigo-100 dark:bg-indigo-900/40 text-indigo-800 dark:text-indigo-300',
    out_for_delivery: 'bg-purple-100 dark:bg-purple-900/40 text-purple-800 dark:text-purple-300',
    delivered: 'bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-300',
    rto: 'bg-orange-100 dark:bg-orange-900/40 text-orange-800 dark:text-orange-300',
    cancelled: 'bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-300',
    exception: 'bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-300',
  })[status] || 'bg-muted text-foreground';

const EMPTY_FORM = {
  carrier_id: '',
  awb: '',
  tracking_url: '',
  weight_kg: '',
  shipping_charge: '',
  estimated_delivery: '',
  notes: '',
  send_email: true,
};

const ShipmentPanel = ({ orderId, isInternational, onOrderChanged }) => {
  const toast = useToast();
  const [shipments, setShipments] = useState([]);
  const [carriers, setCarriers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [generatingLabelId, setGeneratingLabelId] = useState(null);

  // ShipGlobal-only booking inputs — a gift/commercial flag (drives csb5_status) and a
  // service choice, picked from a live rate lookup rather than typed or defaulted.
  const [isGift, setIsGift] = useState(false);
  const [rates, setRates] = useState([]);
  const [loadingRates, setLoadingRates] = useState(false);
  const [selectedService, setSelectedService] = useState('');

  // Shiprocket-only booking input — a courier choice, picked from a live rate lookup.
  const [shiprocketRates, setShiprocketRates] = useState([]);
  const [loadingShiprocketRates, setLoadingShiprocketRates] = useState(false);
  const [selectedCourierId, setSelectedCourierId] = useState('');
  // Parcel as typed here (blank = use the products' values); packageInfo is what the server resolved,
  // including the volumetric and applicable (billed) weight.
  const [pkg, setPkg] = useState({ weight_kg: '', length_cm: '', breadth_cm: '', height_cm: '' });
  const [packageInfo, setPackageInfo] = useState(null);
  // Gift/sample orders only: what the parcel is declared at to the courier. Blank = the order's own
  // value. Shiprocket's charges follow it, and the goods are free, so it can be lowered.
  const [declaredValue, setDeclaredValue] = useState('');
  const [declaredInfo, setDeclaredInfo] = useState({ value: null, editable: false });
  const [codRates, setCodRates] = useState(false);

  // Even when a carrier can create the shipment via its API, the admin may have already
  // booked it directly on the carrier's own site/dashboard (or the live rate lookup may
  // be failing) — this lets them fall back to typing the AWB by hand instead of being
  // forced through the API path.
  const [manualOverride, setManualOverride] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [shipmentData, carrierData] = await Promise.allSettled([
        adminShippingService.getShipments(orderId),
        adminShippingService.getCarriers(true),
      ]);
      if (shipmentData.status === 'fulfilled') setShipments(shipmentData.value || []);
      if (carrierData.status === 'fulfilled') setCarriers(carrierData.value || []);
    } catch (err) {
      toast.error('Failed to load shipments');
    } finally {
      setLoading(false);
    }
  }, [orderId, toast]);

  useEffect(() => {
    load();
  }, [load]);

  // Surface the carriers that serve this order's lane first, but never hide the rest —
  // an international order occasionally ships domestically to a forwarder.
  const selectedCarrier = carriers.find((c) => c.id === form.carrier_id);
  const laneCarriers = carriers.filter((c) => (isInternational ? c.supports_international : c.supports_domestic));
  const otherCarriers = carriers.filter((c) => !laneCarriers.includes(c));

  // Carriers with a live API integration can create the shipment themselves — the admin
  // doesn't need an AWB because the carrier allocates one — unless they've flipped
  // manualOverride, e.g. because they already booked it directly on the carrier's site.
  const canCreateViaApi = !!selectedCarrier?.api_enabled && selectedCarrier?.provider !== 'manual';
  const useApi = canCreateViaApi && !manualOverride;
  const isShipGlobal = selectedCarrier?.provider === 'shipglobal';
  const isShiprocket = selectedCarrier?.provider === 'shiprocket';

  const loadRates = useCallback(async () => {
    if (!form.carrier_id) return;
    setLoadingRates(true);
    setRates([]);
    setSelectedService('');
    try {
      const options = await adminShippingService.getShipGlobalRates(orderId, form.carrier_id);
      setRates(options || []);
    } catch (err) {
      toast.error(err.message || 'Failed to fetch ShipGlobal rates');
    } finally {
      setLoadingRates(false);
    }
  }, [orderId, form.carrier_id, toast]);

  const loadShiprocketRates = useCallback(async () => {
    if (!form.carrier_id) return;
    setLoadingShiprocketRates(true);
    setShiprocketRates([]);
    setSelectedCourierId('');
    try {
      const result = await adminShippingService.getShiprocketRates(orderId, form.carrier_id, pkg, declaredValue);
      setShiprocketRates(result?.couriers || []);
      setDeclaredInfo({ value: result?.declared_value ?? null, editable: !!result?.declared_value_editable });
      setPackageInfo(result?.package || null);
      setCodRates(!!result?.cod);
    } catch (err) {
      toast.error(err.message || 'Failed to fetch Shiprocket rates');
    } finally {
      setLoadingShiprocketRates(false);
    }
  }, [orderId, form.carrier_id, pkg, declaredValue, toast]);

  // Fetch rates as soon as ShipGlobal or Shiprocket is picked — the admin needs a
  // service/courier chosen before they can book anyway, so there's no reason to make
  // this a separate click.
  // Re-quote shortly after the parcel values change: the billed weight, and so every rate, depends on them.
  useEffect(() => {
    if (!isShiprocket) return undefined;
    const timer = setTimeout(loadShiprocketRates, 600);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pkg, declaredValue]);

  useEffect(() => {
    if (isShipGlobal) {
      loadRates();
    } else if (isShiprocket) {
      loadShiprocketRates();
    } else {
      setRates([]);
      setSelectedService('');
      setIsGift(false);
      setShiprocketRates([]);
      setSelectedCourierId('');
    }
    setManualOverride(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isShipGlobal, isShiprocket, form.carrier_id]);

  const handleCreateViaApi = async () => {
    if (!form.carrier_id) {
      toast.error('Select a shipping partner');
      return;
    }
    if (isShipGlobal && !selectedService) {
      toast.error('Select a ShipGlobal service');
      return;
    }
    setSaving(true);
    try {
      const shipment = await adminShippingService.createShipmentViaCarrier(
        orderId,
        form.carrier_id,
        form.send_email,
        isShipGlobal
          ? { csb5Status: isGift ? 0 : 1, service: selectedService }
          : isShiprocket
            ? { courierId: selectedCourierId, package: pkg, declaredValue: declaredInfo.editable ? declaredValue : '' }
            : {},
      );
      toast.success(`${shipment.courier_name || 'Carrier'} assigned AWB ${shipment.awb}`);
      setForm(EMPTY_FORM);
      setShowForm(false);
      setIsGift(false);
      setSelectedService('');
      await load();
      onOrderChanged?.();
    } catch (err) {
      // The carrier's own validation message names the offending field — show it whole
      // rather than a generic failure, since that's what makes it fixable.
      toast.error(err.message || 'Carrier rejected the shipment');
    } finally {
      setSaving(false);
    }
  };

  const handleGenerateLabel = async (shipment) => {
    if (
      !window.confirm(`This charges the carrier's wallet to purchase a real label for AWB ${shipment.awb}. Continue?`)
    ) {
      return;
    }
    setGeneratingLabelId(shipment.id);
    try {
      await adminShippingService.generateLabel(shipment.id);
      toast.success('Label purchased');
      await load();
    } catch (err) {
      toast.error(err.message || 'Failed to generate label');
    } finally {
      setGeneratingLabelId(null);
    }
  };

  const handleCreate = async () => {
    if (!form.awb.trim()) {
      toast.error('Tracking number (AWB) is required');
      return;
    }
    if (!form.carrier_id) {
      toast.error('Select a shipping partner');
      return;
    }

    setSaving(true);
    try {
      await adminShippingService.createShipment(orderId, {
        carrier_id: form.carrier_id,
        awb: form.awb.trim(),
        tracking_url: form.tracking_url.trim() || null,
        weight_kg: form.weight_kg ? Number(form.weight_kg) : null,
        shipping_charge: form.shipping_charge ? Number(form.shipping_charge) : null,
        estimated_delivery: form.estimated_delivery || null,
        notes: form.notes.trim() || null,
        send_email: form.send_email,
      });
      toast.success(
        form.send_email ? 'Shipment saved — tracking email sent to the customer' : 'Shipment saved (no email sent)',
      );
      setForm(EMPTY_FORM);
      setShowForm(false);
      await load();
      onOrderChanged?.();
    } catch (err) {
      toast.error(err.message || 'Failed to save shipment');
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = async (shipment, status) => {
    try {
      await adminShippingService.updateShipment(shipment.id, { status });
      toast.success('Shipment status updated');
      await load();
      onOrderChanged?.();
    } catch (err) {
      toast.error(err.message || 'Failed to update shipment');
    }
  };

  const handleResend = async (shipment) => {
    try {
      await adminShippingService.resendTrackingEmail(shipment.id);
      toast.success('Tracking email queued');
      // The send happens in the background, so give it a beat before re-reading the
      // sent timestamp rather than showing a stale "never sent".
      setTimeout(load, 2000);
    } catch (err) {
      toast.error(err.message || 'Failed to resend email');
    }
  };

  // Two clicks: the first arms the button, the second cancels - cancelling tells the courier too.
  const [confirmCancelId, setConfirmCancelId] = useState(null);
  const handleCancel = async (shipment) => {
    if (confirmCancelId !== shipment.id) {
      setConfirmCancelId(shipment.id);
      return;
    }
    setConfirmCancelId(null);
    try {
      await adminShippingService.cancelShipment(shipment.id);
      toast.success('Shipment cancelled');
      await load();
      onOrderChanged?.();
    } catch (err) {
      toast.error(err.message || 'Failed to cancel the shipment');
    }
  };

  const handleDelete = async (shipment) => {
    try {
      await adminShippingService.deleteShipment(shipment.id);
      toast.success('Shipment deleted');
      await load();
      onOrderChanged?.();
    } catch (err) {
      toast.error(err.message || 'Failed to delete shipment');
    }
  };

  const formatDate = (value) =>
    value
      ? new Date(value).toLocaleString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        })
      : null;

  return (
    <div className="border rounded-lg border-border p-4">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h3 className="text-lg font-medium flex items-center gap-2">
          <Truck size={20} />
          Shipments
        </h3>
        <div className="flex items-center gap-2">
          <button
            onClick={load}
            disabled={loading}
            className="p-2 text-muted-foreground hover:text-foreground disabled:opacity-50"
            title="Refresh"
          >
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          </button>
          {!showForm && (
            <button
              onClick={() => setShowForm(true)}
              className="px-3 py-1.5 text-sm bg-primary text-primary-foreground hover:bg-primary/90 flex items-center gap-1"
            >
              <Plus size={14} />
              Add Shipment
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <p className="text-sm text-muted-foreground/70">Loading shipments...</p>
      ) : (
        <>
          {shipments.length === 0 && !showForm && (
            <p className="text-sm text-muted-foreground/70">
              No shipment recorded yet. Add one to notify the customer with a tracking link.
            </p>
          )}

          <div className="space-y-3">
            {shipments.map((shipment) => (
              <div key={shipment.id} className="border rounded-lg border-border p-3 text-sm">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      {shipment.carrier_logo_url && (
                        <img
                          src={shipment.carrier_logo_url}
                          alt={shipment.courier_name || 'Carrier'}
                          className="h-5 w-auto object-contain"
                        />
                      )}
                      <span className="font-medium">{shipment.courier_name || 'Courier'}</span>
                      <span className={`text-xs px-2 py-0.5 ${statusColor(shipment.status)}`}>
                        {shipment.status?.replace(/_/g, ' ')}
                      </span>
                    </div>
                    <p className="text-muted-foreground mt-1 font-mono text-xs break-all">{shipment.awb}</p>
                    {shipment.estimated_delivery && (
                      <p className="text-muted-foreground text-xs mt-1">Est. delivery: {shipment.estimated_delivery}</p>
                    )}
                    <ShipmentCharges
                      shipment={shipment}
                      onSaved={async () => {
                        await load();
                        onOrderChanged?.();
                      }}
                    />
                    <p className="text-xs mt-1">
                      {shipment.tracking_email_sent_at ? (
                        <span className="text-green-700 dark:text-green-300">
                          Tracking email sent {formatDate(shipment.tracking_email_sent_at)}
                        </span>
                      ) : (
                        <span className="text-amber-700 dark:text-amber-300">Tracking email not sent yet</span>
                      )}
                    </p>
                    {shipment.provider === 'shipglobal' &&
                      (shipment.label_url ? (
                        <a
                          href={shipment.label_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-blue-700 dark:text-blue-300 hover:underline mt-1 inline-block"
                        >
                          View label
                        </a>
                      ) : (
                        <button
                          onClick={() => handleGenerateLabel(shipment)}
                          disabled={generatingLabelId === shipment.id}
                          className="text-xs text-amber-800 dark:text-amber-300 bg-amber-50 dark:bg-amber-950/50 border rounded-md border-amber-200 dark:border-amber-900 px-2 py-1 mt-1 hover:bg-amber-100 dark:hover:bg-amber-900/60 disabled:opacity-50"
                        >
                          {generatingLabelId === shipment.id ? 'Purchasing label...' : 'Generate & Pay for Label'}
                        </button>
                      ))}
                  </div>

                  <div className="flex items-center gap-1 shrink-0">
                    {shipment.tracking_url && (
                      <a
                        href={shipment.tracking_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2 text-muted-foreground hover:text-foreground"
                        title="Open tracking page"
                      >
                        <ExternalLink size={16} />
                      </a>
                    )}
                    <button
                      onClick={() => handleResend(shipment)}
                      className="p-2 text-muted-foreground hover:text-foreground"
                      title="Resend tracking email"
                    >
                      <Mail size={16} />
                    </button>
                    {shipment.status !== 'cancelled' && (
                      <button
                        onClick={() => handleCancel(shipment)}
                        onBlur={() => setConfirmCancelId(null)}
                        className={`p-2 text-xs flex items-center gap-1 ${
                          confirmCancelId === shipment.id
                            ? 'text-red-600 dark:text-red-400 font-medium'
                            : 'text-muted-foreground hover:text-red-600 dark:hover:text-red-400'
                        }`}
                        title="Cancel this shipment with the courier (Shiprocket) too"
                      >
                        <Ban size={16} />
                        {confirmCancelId === shipment.id && 'Confirm cancel'}
                      </button>
                    )}
                    <button
                      onClick={() => handleDelete(shipment)}
                      className="p-2 text-muted-foreground hover:text-red-600 dark:hover:text-red-400"
                      title="Delete shipment"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>

                <div className="mt-3 flex items-center gap-2">
                  <label className="text-xs text-muted-foreground">Status</label>
                  <select
                    value={shipment.status}
                    onChange={(e) => handleStatusChange(shipment, e.target.value)}
                    className="px-2 py-1 text-xs border rounded-md border-input focus:outline-none focus:border-foreground"
                  >
                    {SHIPMENT_STATUSES.map((s) => (
                      <option key={s.value} value={s.value}>
                        {s.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            ))}
          </div>

          {showForm && (
            <div className="mt-4 border rounded-lg border-input bg-muted/50 p-4 space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium mb-1">Shipping Partner *</label>
                  <select
                    value={form.carrier_id}
                    onChange={(e) => setForm({ ...form, carrier_id: e.target.value })}
                    className="w-full px-3 py-2 text-sm border rounded-lg border-input focus:outline-none focus:border-foreground"
                  >
                    <option value="">Select a partner...</option>
                    {laneCarriers.length > 0 && (
                      <optgroup label={isInternational ? 'International' : 'Domestic'}>
                        {laneCarriers.map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.name}
                          </option>
                        ))}
                      </optgroup>
                    )}
                    {otherCarriers.length > 0 && (
                      <optgroup label="Other">
                        {otherCarriers.map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.name}
                          </option>
                        ))}
                      </optgroup>
                    )}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Tracking Number (AWB){!useApi && ' *'}</label>
                  <input
                    type="text"
                    value={form.awb}
                    onChange={(e) => setForm({ ...form, awb: e.target.value })}
                    placeholder={useApi ? 'Assigned automatically' : 'e.g. 1234567890'}
                    disabled={useApi}
                    className="w-full px-3 py-2 text-sm border rounded-lg border-input focus:outline-none focus:border-foreground disabled:bg-muted disabled:text-muted-foreground/70"
                  />
                  {canCreateViaApi && (
                    <label className="flex items-center gap-1 mt-1 text-xs text-muted-foreground">
                      <input
                        type="checkbox"
                        checked={manualOverride}
                        onChange={(e) => setManualOverride(e.target.checked)}
                      />
                      Already booked directly with the carrier — enter the AWB manually instead
                    </label>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Parcel Weight (kg)</label>
                  <input
                    type="number"
                    step="0.001"
                    min="0"
                    value={form.weight_kg}
                    onChange={(e) => setForm({ ...form, weight_kg: e.target.value })}
                    placeholder="e.g. 1.250"
                    className="w-full px-3 py-2 text-sm border rounded-lg border-input focus:outline-none focus:border-foreground"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Shipping Cost ({'₹'})</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={form.shipping_charge}
                    onChange={(e) => setForm({ ...form, shipping_charge: e.target.value })}
                    placeholder="What the carrier actually charged, once known"
                    className="w-full px-3 py-2 text-sm border rounded-lg border-input focus:outline-none focus:border-foreground"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Estimated Delivery</label>
                  <input
                    type="date"
                    value={form.estimated_delivery}
                    onChange={(e) => setForm({ ...form, estimated_delivery: e.target.value })}
                    className="w-full px-3 py-2 text-sm border rounded-lg border-input focus:outline-none focus:border-foreground"
                  />
                </div>
              </div>

              {/* Only needed when the chosen carrier has no tracking URL pattern configured. */}
              {form.carrier_id && !selectedCarrier?.tracking_url_pattern && (
                <div>
                  <label className="block text-sm font-medium mb-1">Tracking URL</label>
                  <input
                    type="url"
                    value={form.tracking_url}
                    onChange={(e) => setForm({ ...form, tracking_url: e.target.value })}
                    placeholder="https://..."
                    className="w-full px-3 py-2 text-sm border rounded-lg border-input focus:outline-none focus:border-foreground"
                  />
                  <p className="text-xs text-amber-700 dark:text-amber-300 mt-1">
                    {selectedCarrier?.name} has no tracking URL pattern set. Paste the full tracking link here, or set
                    the pattern once on the Shipping Partners page so it builds automatically from now on.
                  </p>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium mb-1">Internal Notes</label>
                <textarea
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  rows={2}
                  placeholder="Not shown to the customer"
                  className="w-full px-3 py-2 text-sm border rounded-lg border-input focus:outline-none focus:border-foreground"
                />
              </div>

              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={form.send_email}
                  onChange={(e) => setForm({ ...form, send_email: e.target.checked })}
                />
                Email the customer their tracking link now
              </label>

              {useApi && !isShipGlobal && !isShiprocket && (
                <p className="text-xs text-muted-foreground bg-card border rounded-md border-border p-2">
                  {selectedCarrier.name} will create the shipment and assign an AWB automatically. Weight and dimensions
                  come from the products on this order.
                </p>
              )}

              {isShipGlobal && !manualOverride && (
                <div className="border rounded-lg border-input bg-card p-3 space-y-3">
                  <label className="flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={isGift} onChange={(e) => setIsGift(e.target.checked)} />
                    Non-commercial / gift shipment (e.g. an influencer send)
                  </label>
                  <p className="text-xs text-muted-foreground -mt-2">
                    Filed as{' '}
                    {isGift
                      ? 'CSB-IV — non-commercial, no export incentive'
                      : 'CSB-V — commercial, RoDTEP/drawback-eligible'}
                    . Declared value is always the real product price either way.
                  </p>

                  <div>
                    <div className="flex flex-wrap items-center justify-between gap-3 mb-1">
                      <label className="block text-sm font-medium">ShipGlobal Service *</label>
                      <button
                        type="button"
                        onClick={loadRates}
                        disabled={loadingRates}
                        className="text-xs text-blue-700 dark:text-blue-300 hover:underline disabled:opacity-50"
                      >
                        {loadingRates ? 'Refreshing...' : 'Refresh rates'}
                      </button>
                    </div>
                    {loadingRates ? (
                      <p className="text-xs text-muted-foreground/70">Fetching rates for this destination...</p>
                    ) : rates.length === 0 ? (
                      <p className="text-xs text-amber-700 dark:text-amber-300">
                        No services available. Check the destination postcode, or refresh.
                      </p>
                    ) : (
                      <select
                        value={selectedService}
                        onChange={(e) => setSelectedService(e.target.value)}
                        className="w-full px-3 py-2 text-sm border rounded-lg border-input focus:outline-none focus:border-foreground"
                      >
                        <option value="">Select a service...</option>
                        {rates.map((r) => (
                          <option key={r.title} value={r.title}>
                            {r.title} — {r.currency} {r.price} ({r.transit_time})
                          </option>
                        ))}
                      </select>
                    )}
                  </div>

                  <p className="text-xs text-muted-foreground">
                    Weight and dimensions come from the products on this order. Booking only creates the order and
                    assigns a tracking number — the paid label is a separate step afterward.
                  </p>
                </div>
              )}

              {isShiprocket && !manualOverride && (
                <div className="border rounded-lg border-input bg-card p-3 space-y-3">
                  <div className="flex flex-wrap items-center justify-between gap-3 mb-1">
                    <label className="block text-sm font-medium">Shiprocket Courier *</label>
                    <button
                      type="button"
                      onClick={loadShiprocketRates}
                      disabled={loadingShiprocketRates}
                      className="text-xs text-blue-700 dark:text-blue-300 hover:underline disabled:opacity-50"
                    >
                      {loadingShiprocketRates ? 'Refreshing...' : 'Refresh rates'}
                    </button>
                  </div>
                  <div>
                    <p className="text-sm font-medium mb-1">Package</p>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                      {[
                        ['weight_kg', 'Dead weight (kg)'],
                        ['length_cm', 'Length (cm)'],
                        ['breadth_cm', 'Breadth (cm)'],
                        ['height_cm', 'Height (cm)'],
                      ].map(([key, label]) => (
                        <input
                          key={key}
                          type="number"
                          min="0"
                          step="0.01"
                          aria-label={label}
                          title={label}
                          placeholder={packageInfo?.[key] ? `${label}: ${packageInfo[key]}` : label}
                          value={pkg[key]}
                          onChange={(e) => setPkg({ ...pkg, [key]: e.target.value })}
                          className="w-full px-3 py-2 text-sm border rounded-lg border-input focus:outline-none focus:border-foreground"
                        />
                      ))}
                    </div>
                    {packageInfo && (
                      <p className="text-xs text-muted-foreground mt-1">
                        Volumetric {packageInfo.volumetric_weight_kg} kg · Billed (higher of dead and volumetric){' '}
                        <span className="font-medium text-foreground">{packageInfo.applicable_weight_kg} kg</span>
                        {codRates && ' · Couriers shown can collect cash on delivery'}
                      </p>
                    )}
                  </div>
                  {declaredInfo.editable && (
                    <div>
                      <label className="text-sm font-medium block mb-1" htmlFor="declared-value">
                        Declared value (₹)
                      </label>
                      <input
                        id="declared-value"
                        type="number"
                        min="1"
                        step="1"
                        placeholder={declaredInfo.value ? `Order value: ${declaredInfo.value}` : 'Order value'}
                        value={declaredValue}
                        onChange={(e) => setDeclaredValue(e.target.value)}
                        className="w-full sm:w-48 px-3 py-2 text-sm border rounded-lg border-input focus:outline-none focus:border-foreground"
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        {declaredValue
                          ? `The courier is told this parcel is worth ₹${declaredValue}; rates above are for that value.`
                          : 'Gift, so no sale. A lower declared value lowers the courier charge. Rates below follow it.'}{' '}
                        It also caps any courier insurance claim.
                      </p>
                    </div>
                  )}
                  {loadingShiprocketRates ? (
                    <p className="text-xs text-muted-foreground/70">Fetching couriers for this destination...</p>
                  ) : shiprocketRates.length === 0 ? (
                    <p className="text-xs text-amber-700 dark:text-amber-300">
                      No couriers available. Check the destination pincode, or refresh.
                    </p>
                  ) : (
                    <select
                      value={selectedCourierId}
                      onChange={(e) => setSelectedCourierId(e.target.value)}
                      className="w-full px-3 py-2 text-sm border rounded-lg border-input focus:outline-none focus:border-foreground"
                    >
                      <option value="">Select a courier...</option>
                      {shiprocketRates.map((r) => (
                        <option key={r.courier_id} value={r.courier_id}>
                          {r.courier_name} — ₹{r.rate} ({r.etd})
                        </option>
                      ))}
                    </select>
                  )}
                </div>
              )}

              <div className="flex gap-2 pt-1">
                <button
                  onClick={useApi ? handleCreateViaApi : handleCreate}
                  disabled={
                    saving ||
                    (useApi && isShipGlobal && !selectedService) ||
                    (useApi && isShiprocket && !selectedCourierId)
                  }
                  className="px-4 py-2 text-sm bg-primary text-primary-foreground hover:bg-primary/90 disabled:bg-muted-foreground/50"
                >
                  {saving ? 'Saving...' : useApi ? `Create with ${selectedCarrier.name}` : 'Save Shipment'}
                </button>
                <button
                  onClick={() => {
                    setShowForm(false);
                    setForm(EMPTY_FORM);
                  }}
                  disabled={saving}
                  className="px-4 py-2 text-sm border rounded-lg border-input hover:bg-accent"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ShipmentPanel;
